import { useState } from 'react';
import { commissionApi } from '../api';
import { useAuth } from '../AuthContext';
import { publicBranchName } from '../public/PublicBrand';
import { printTable } from '../components/exportCsv';
import { IconDots, IconPrinter, IconSearch } from '../components/icons';
import {
  Badge, Empty, ErrorNote, Modal, Spinner, TH_LOCALE, dayLabel, money, money2, th,
  todayIso, useLoader,
} from '../components/common';

/** 'พุธ 29 ก.ค. 2026' — the pill that heads every ประวัติการให้บริการ group. */
const dayPill = (dayIso) =>
  `${dayLabel(dayIso, TH_LOCALE, { weekday: 'long' }).replace(/^วัน/, '')} `
  + dayLabel(dayIso, TH_LOCALE, { day: 'numeric', month: 'short', year: 'numeric' });

/** Minutes as the shop reads them — 1059:40, not 1059.67. */
const hhmm = (minutes) =>
  `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;

/**
 * ค่ามือ — the payout screen.
 *
 * One white sheet in three columns: the therapists on the left, their
 * ประวัติการให้บริการ day by day in the middle, and รายการแยกตามบริการ over the
 * summary box that เคลียร์ค่ามือ acts on down the right.
 */
export default function Commission() {
  const { branches } = useAuth();
  const [tab, setTab] = useState('outstanding');
  const [branchScope, setBranchScope] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [periodEnd, setPeriodEnd] = useState(todayIso());
  const [settling, setSettling] = useState(false);
  const [adjusting, setAdjusting] = useState(null);   // the day being edited
  const [search, setSearch] = useState('');
  const [qrOpen, setQrOpen] = useState(false);

  const list = useLoader(
    () => commissionApi.outstanding(branchScope),
    [branchScope],
  );
  const detail = useLoader(
    () => (selectedId
      ? commissionApi.therapist(selectedId, periodEnd, branchScope)
      : Promise.resolve(null)),
    [selectedId, periodEnd, branchScope],
  );
  const history = useLoader(
    () => (tab === 'history'
      ? commissionApi.history(selectedId, branchScope)
      : Promise.resolve(null)),
    [tab, selectedId, branchScope],
  );

  const settle = async () => {
    if (!selectedId) return;
    setSettling(true);
    try {
      await commissionApi.settle(selectedId, periodEnd, branchScope);
      list.reload();
      detail.reload();
    } catch (err) {
      alert(err.message);
    } finally {
      setSettling(false);
    }
  };

  if (list.loading) return <Spinner />;
  if (list.error) return <ErrorNote error={list.error} />;

  const q = search.trim().toLowerCase();
  const therapists = list.data.therapists.filter((t) => !q ||
    `${t.nicknameEn || ''} ${t.nicknameTh || ''} ${t.code || ''}`.toLowerCase().includes(q));
  const groups = therapists.reduce((acc, t) => {
    (acc[t.employmentType] ||= []).push(t);
    return acc;
  }, {});

  const orgBranches = list.data.branches || [];
  const summary = detail.data?.summary;
  const days = detail.data?.days || [];

  // The API pools every branch in the organisation, so the heading can only
  // name one when there is only one to name.
  const scope = orgBranches.length === 1 ? `สาขา ${publicBranchName(orgBranches[0])}` : 'ทุกสาขา';

  // One line per service, not one per service *and branch*: the branch split is
  // already carried by the day rows and the summary box.
  const byService = Object.values(
    (detail.data?.byService || []).reduce((acc, s) => {
      const row = (acc[s.serviceVariantId] ||= { ...s, timesPerformed: 0, totalMinutes: 0 });
      row.timesPerformed += s.timesPerformed;
      row.totalMinutes += s.totalMinutes;
      return acc;
    }, {}),
  ).sort((a, b) => `${a.nameEn}`.localeCompare(`${b.nameEn}`));

  const serviceTotal = byService.reduce(
    (acc, s) => ({ times: acc.times + s.timesPerformed, minutes: acc.minutes + s.totalMinutes }),
    { times: 0, minutes: 0 },
  );

  const selected = therapists.find((x) => x.therapistId === selectedId);

  return (
    <>
      <div className="toolbar cm-toolbar">
        <button
          className={`cm-tab${tab === 'outstanding' ? ' active' : ''}`}
          onClick={() => setTab('outstanding')}
        >
          ค่ามือค้างชำระ
        </button>
        <button
          className={`cm-tab${tab === 'history' ? ' active' : ''}`}
          onClick={() => setTab('history')}
        >
          ประวัติค่ามือ
        </button>

        <select
          className="chip"
          value={branchScope}
          onChange={(e) => setBranchScope(e.target.value)}
          aria-label="เลือกสาขาค่ามือ"
        >
          <option value="all">ทุกสาขา</option>
          {branches.map((b) => (
            <option key={b.id} value={b.id}>สาขา {b.displayName || b.code}</option>
          ))}
        </select>

        <span className="cm-date-label">เลือกวันสุดท้ายในการเคลียร์ค่ามือ</span>
        <input
          className="cm-date"
          type="date"
          value={periodEnd}
          onChange={(e) => setPeriodEnd(e.target.value)}
        />

        <div style={{ flex: 1 }} />
        <button
          className="btn-outline cm-print"
          disabled={!days.length}
          onClick={() => {
            printTable(
              `รายการค่ามือ — ${selected?.nicknameEn || selected?.nicknameTh || ''}`,
              [
                { label: 'วันที่', value: (r) => r.workDate },
                { label: 'ครั้ง', value: (r) => r.serviceCount, num: true },
                { label: 'ค่ามือ', value: (r) => money(r.commissionTotal), num: true },
                { label: 'ประกันมือ', value: (r) => money(r.dailyGuarantee), num: true },
                { label: 'ส่วนต่าง', value: (r) => money(r.topupAmount), num: true },
                { label: 'ค่ารีเควส', value: (r) => money(r.requestFeeTotal), num: true },
                { label: 'ต้องจ่าย', value: (r) => money(r.payableAmount), num: true },
              ],
              days,
              `ถึงวันที่ ${periodEnd} · รวมที่ต้องจ่าย ${money(summary?.netPayable)}`,
            );
          }}
        >
          <IconPrinter size={16} /> พิมพ์รายการค่ามือหมอ
        </button>
      </div>

      {adjusting && (
        <CommissionDayDialog
          therapistId={selectedId}
          therapist={selected}
          day={adjusting}
          adjustments={(detail.data?.adjustments || []).filter((a) =>
            String(a.branch_id) === String(adjusting.branchId)
            && String(a.effective_date) === String(adjusting.workDate))}
          onClose={() => setAdjusting(null)}
          onSaved={() => { setAdjusting(null); detail.reload(); list.reload(); }}
        />
      )}

      {qrOpen && selected?.promptPayQrUrl && (
        <Modal
          title={`QR พร้อมเพย์ — ${selected.nicknameEn || selected.nicknameTh || selected.code}`}
          onClose={() => setQrOpen(false)}
        >
          <div className="cm-qr-modal">
            <img src={selected.promptPayQrUrl} alt="QR พร้อมเพย์" />
            <div className="cm-qr-modal-total">
              ยอดค่ามือที่ต้องจ่าย <b>{money(summary?.netPayable)}</b>
            </div>
            <span>สแกนและตรวจสอบชื่อผู้รับในแอปธนาคารก่อนยืนยันการโอน</span>
          </div>
        </Modal>
      )}

      <div className="cm-sheet">
        <div className="cm-col cm-people">
          <label className="search-field cm-search">
            <IconSearch size={17} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="ค้นหาหมอนวด"
            />
          </label>
          <div className="cm-people-title">ประเภทหมอนวด {scope}</div>
          {Object.entries(groups).map(([type, rows]) => (
            <div key={type}>
              <div className="cm-type"><span className="cm-tag">{th(type)}</span></div>
              {rows.map((t) => (
                <button
                  key={t.therapistId}
                  type="button"
                  className={`cm-person${selectedId === t.therapistId ? ' on' : ''}`}
                  onClick={() => { setSelectedId(t.therapistId); setQrOpen(false); }}
                >
                  <span className="cm-face">
                    {t.photoUrl && <img src={t.photoUrl} alt="" />}
                  </span>
                  <span className="cm-person-name">
                    {t.nicknameEn || t.nicknameTh}
                    {t.nicknameEn && t.nicknameTh ? ` (${t.nicknameTh})` : ''}
                  </span>
                  {t.netPayable > 0 && <span className="cm-due">ค้างชำระ</span>}
                </button>
              ))}
            </div>
          ))}
        </div>

        {tab === 'history' ? (
          <div className="cm-col cm-wide">
            <HistoryPanel data={history.data} loading={history.loading} />
          </div>
        ) : (
          <>
            <div className="cm-col cm-days">
              <div className="cm-title">ประวัติการให้บริการ</div>
              {detail.loading ? (
                <Spinner />
              ) : !selectedId ? (
                <Empty icon="staff" message="เลือกหมอนวดเพื่อดูรายละเอียด" />
              ) : !days.length ? (
                <Empty icon="payment" message="ไม่มีรายการค้างชำระ" />
              ) : (
                days.map((d) => (
                    <div className="cm-day" key={`${d.branchId}-${d.workDate}`}>
                      <div className="cm-day-head">
                        <span className="cm-tag">{dayPill(d.workDate)}</span>
                        {orgBranches.length > 1 && d.branchCode && (
                          <span className="cm-tag ghost">{publicBranchName(d.branchCode)}</span>
                        )}
                        <div className="cm-day-figs">
                          <div className="cm-day-payable">
                            ค่ามือที่ต้องจ่าย <b>{money2(d.payableAmount)}</b>
                          </div>
                          {/* The bigger of the two is the one the day is
                              actually paid on, so that is the one picked out. */}
                          <div className="cm-day-basis">
                            <span className={Number(d.commissionTotal) < Number(d.dailyGuarantee) ? 'on' : ''}>
                              ประกันมือต่อวัน : {money2(d.dailyGuarantee)}
                            </span>
                            {' | '}
                            <span className={Number(d.commissionTotal) >= Number(d.dailyGuarantee) ? 'on' : ''}>
                              ค่ามือรวม : {money2(d.commissionTotal)}
                            </span>
                            {Number(d.specialAdjustmentTotal) !== 0 && (
                              <>
                                {' | '}
                                <span className={Number(d.specialAdjustmentTotal) < 0 ? 'neg' : 'on'}>
                                  รายการพิเศษ : {Number(d.specialAdjustmentTotal) > 0 ? '+' : ''}
                                  {money2(d.specialAdjustmentTotal)}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="row-menu">
                          <button
                            className="cm-day-more"
                            title="แก้ไขค่ามือ"
                            onClick={() => setAdjusting(d)}
                          >
                            <IconDots size={18} />
                          </button>
                        </div>
                      </div>

                      {/* One row per completed treatment, including repeats.
                          The amount is the branch's configured ค่ามือ snapshot,
                          exactly as the original service history displays it. */}
                      {(d.services || []).length ? d.services.map((service) => (
                        <div className="cm-line cm-service-entry" key={service.appointmentId}>
                          <span>
                            {service.nameEn} {service.nameTh} {service.durationMinutes} นาที
                          </span>
                          <span className="cm-line-amt">{money2(service.commissionAmount)}</span>
                        </div>
                      )) : d.empty ? (
                        <div className="cm-line muted">
                          <span>ไม่มีรายการให้บริการ</span>
                          <span className="cm-line-amt">0.00</span>
                        </div>
                      ) : (
                        <div className="cm-line">
                          <span>{d.serviceCount} ครั้ง · {hhmm(d.serviceMinutes)} ชม.</span>
                          <span className="cm-line-amt">{money2(d.commissionTotal)}</span>
                        </div>
                      )}
                    </div>
                ))
              )}
            </div>

            <div className="cm-col cm-money">
              <div className="cm-title">รายการแยกตามบริการ</div>
              <div className="cm-svc-all">
                <span>ทั้งหมด</span>
                <b>{serviceTotal.times} ครั้ง {hhmm(serviceTotal.minutes)} ชม.</b>
              </div>
              <div className="cm-svc-list">
                {!byService.length ? <Empty icon="commission" /> : byService.map((s) => (
                  <div className="cm-line" key={s.serviceVariantId}>
                    <span>{s.nameEn} {s.nameTh} {s.durationMinutes} นาที</span>
                    <span className="cm-line-amt">{s.timesPerformed} ครั้ง</span>
                  </div>
                ))}
              </div>

              <div className="cm-summary">
                <div className="cm-sum-row">
                  <span>ประกันมือต่อวัน</span>
                  <span>{money(detail.data?.dailyGuarantee)}</span>
                </div>
                <div className="cm-rule" />

                <div className="cm-sum-row"><span>ค่ามือ</span><span>{money(summary?.commissionTotal)}</span></div>
                <div className="cm-sum-row"><span>ส่วนต่างประกันมือ</span><span>{money(summary?.guaranteeTopupTotal)}</span></div>
                <div className="cm-sum-row"><span>ค่ารีเควส</span><span>{money(summary?.requestFeeTotal)}</span></div>
                <div className="cm-sum-row"><span>เงินรับพิเศษ</span><span>{money(summary?.additionTotal)}</span></div>
                <div className="cm-sum-row">
                  <span>เงินหักพิเศษ</span>
                  <span className="neg">-{money(summary?.deductionTotal)}</span>
                </div>

                {/* Each one is listed so it can be taken back off again — a
                    typo in a deduction is the therapist's money. */}
                {(detail.data?.adjustments || []).length > 0 && (
                  <div className="adj-list">
                    {detail.data.adjustments.map((a) => (
                      <div className="adj-row" key={a.id}>
                        <span className={a.kind === 'addition' ? 'plus' : 'minus'}>
                          {a.kind === 'addition' ? '+' : '−'}{money(a.amount)}
                        </span>
                        <span className="adj-why">{a.reason || '-'}</span>
                        <span className="adj-when">{a.effective_date}</span>
                        <button
                          className="adj-del"
                          title="ลบ"
                          onClick={async () => {
                            await commissionApi.deleteAdjustment(a.id);
                            detail.reload();
                            list.reload();
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* One therapist, one wage: the branches are listed separately
                    so each till can be reconciled, then pooled into what is
                    actually handed over. The original is one shop, so it has
                    nothing to split. */}
                {detail.data?.byBranch?.length > 1 && detail.data.byBranch.map((b) => (
                  <div className="cm-sum-row muted" key={b.branchId}>
                    <span>{publicBranchName(b)}</span>
                    <span>{money(b.netPayable)}</span>
                  </div>
                ))}

                <div className="cm-rule" />
                <div className="cm-sum-row total">
                  <span>ค่ามือที่ต้องจ่าย</span>
                  <span>{money(summary?.netPayable)}</span>
                </div>
              </div>

              {selected && (
                <div className={`cm-promptpay${selected.promptPayQrUrl ? '' : ' empty'}`}>
                  <div className="cm-promptpay-head">
                    <span>QR พร้อมเพย์</span>
                    <b>{selected.nicknameEn || selected.nicknameTh || selected.code}</b>
                  </div>
                  {selected.promptPayQrUrl ? (
                    <button
                      type="button"
                      className="cm-promptpay-code"
                      onClick={() => setQrOpen(true)}
                      title="ขยาย QR พร้อมเพย์"
                    >
                      <img src={selected.promptPayQrUrl} alt="QR พร้อมเพย์" />
                    </button>
                  ) : (
                    <span className="cm-promptpay-missing">
                      ยังไม่ได้เพิ่ม QR · เพิ่มได้ที่ ตั้งค่า → หมอนวด
                    </span>
                  )}
                  <span className="cm-promptpay-amount">
                    ยอดโอน {money(summary?.netPayable)}
                  </span>
                  {selected.promptPayQrUrl && (
                    <span className="cm-promptpay-help">กดรูปเพื่อขยายสำหรับสแกน</span>
                  )}
                </div>
              )}

              <button
                className="cm-settle"
                disabled={settling || !summary?.netPayable}
                onClick={settle}
              >
                {settling ? 'กำลังเคลียร์…' : 'เคลียร์ค่ามือ'}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function HistoryPanel({ data, loading }) {
  if (loading) return <Spinner />;
  if (!data?.settlements?.length) return <Empty icon="history" message="ยังไม่มีประวัติการเคลียร์ค่ามือ" />;

  return (
    <table className="data">
      <thead>
        <tr>
          <th>ช่วงวันที่</th>
          <th>สาขา</th>
          <th className="num">ครั้ง</th>
          <th className="num">ค่ามือ</th>
          <th className="num">ส่วนต่าง</th>
          <th className="num">จ่ายสุทธิ</th>
          <th>สถานะ</th>
          <th>เคลียร์โดย</th>
        </tr>
      </thead>
      <tbody>
        {data.settlements.map((s) => (
          <tr key={s.id}>
            <td>{s.period_start} → {s.period_end}</td>
            <td><span className="badge badge-grey">{publicBranchName(s.branch_code) || '-'}</span></td>
            <td className="num">{s.service_count}</td>
            <td className="num">{money(s.commission_total)}</td>
            <td className="num">{money(s.guarantee_topup_total)}</td>
            <td className="num" style={{ fontWeight: 700 }}>{money(s.net_payable)}</td>
            <td><Badge value={s.status === 'settled' ? 'paid' : s.status} /></td>
            <td>{s.settled_by_name || '-'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** ThaiHand-style editor: the day's guarantee and every special adjustment live together. */
function CommissionDayDialog({ therapistId, therapist, day, adjustments, onClose, onSaved }) {
  const [dailyGuarantee, setDailyGuarantee] = useState(String(day.dailyGuarantee ?? 0));
  const [rows, setRows] = useState(() => adjustments.map((a) => ({
    key: `saved-${a.id}`,
    kind: a.kind,
    amount: String(a.amount),
    reason: a.reason || '',
  })));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const addRow = (kind) => setRows((current) => [...current, {
    key: `new-${Date.now()}-${current.length}`,
    kind,
    amount: '',
    reason: '',
  }]);
  const changeRow = (key, field, value) => setRows((current) => current.map((row) =>
    row.key === key ? { ...row, [field]: value } : row));
  const removeRow = (key) => setRows((current) => current.filter((row) => row.key !== key));

  const commission = Number(day.commissionTotal || 0);
  const guarantee = Number(dailyGuarantee || 0);
  const additions = rows.filter((r) => r.kind === 'addition')
    .reduce((sum, r) => sum + Number(r.amount || 0), 0);
  const deductions = rows.filter((r) => r.kind === 'deduction')
    .reduce((sum, r) => sum + Number(r.amount || 0), 0);
  const specialTotal = additions - deductions;
  const payable = Math.max(commission, guarantee) + Number(day.requestFeeTotal || 0) + specialTotal;

  const save = async () => {
    if (!Number.isFinite(guarantee) || guarantee < 0) {
      return setError(new Error('จำนวนเงินประกันมือต้องไม่ติดลบ'));
    }
    if (rows.some((row) => !(Number(row.amount) > 0))) {
      return setError(new Error('จำนวนเงินพิเศษทุกรายการต้องมากกว่า 0'));
    }

    setBusy(true);
    setError(null);
    try {
      await commissionApi.updateDay({
        therapistId,
        branchId: Number(day.branchId),
        workDate: day.workDate,
        dailyGuarantee: guarantee,
        adjustments: rows.map((row) => ({
          kind: row.kind,
          amount: Number(row.amount),
          reason: row.reason.trim() || null,
        })),
      });
      onSaved();
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };

  return (
    <Modal title="แก้ไขค่ามือ" onClose={onClose} className="cm-edit-modal">
      <ErrorNote error={error} />

      <div className="cm-edit-meta">
        <span>หมอนวด: <strong>{therapist?.nicknameEn || therapist?.nicknameTh || therapist?.code}</strong></span>
        <span className="cm-edit-date">{dayPill(day.workDate)}</span>
        {day.branchCode && <span className="cm-edit-branch">{publicBranchName(day.branchCode)}</span>}
      </div>

      <label className="cm-edit-fieldline">
          <strong>ประกันมือต่อวัน</strong>
          <input
            type="number"
            min="0"
            step="0.01"
            value={dailyGuarantee}
            onChange={(e) => setDailyGuarantee(e.target.value)}
            autoFocus
          />
      </label>

      <div className="cm-edit-commission">
        <strong>ค่ามือ</strong>
        <b>{money2(commission)}</b>
      </div>

      <div className="cm-edit-special-head">
        <strong>รายการพิเศษ</strong>
      </div>
      <div className="cm-edit-special-actions">
        <button type="button" className="addition" onClick={() => addRow('addition')}>⊕ เพิ่มเงินรับพิเศษ</button>
        <button type="button" className="deduction" onClick={() => addRow('deduction')}>⊖ เพิ่มเงินหักพิเศษ</button>
      </div>

      {!rows.length ? (
        <div className="cm-edit-empty">ยังไม่มีรายการพิเศษ</div>
      ) : rows.map((row) => (
        <div className={`cm-edit-adjustment ${row.kind}`} key={row.key}>
          <input
            value={row.reason}
            onChange={(e) => changeRow(row.key, 'reason', e.target.value)}
            placeholder="รายละเอียด"
          />
          <input
            className="cm-edit-amount"
            type="number"
            min="0"
            step="0.01"
            value={row.amount}
            onChange={(e) => changeRow(row.key, 'amount', e.target.value)}
            placeholder="0.00"
          />
          <button type="button" className="adj-del" onClick={() => removeRow(row.key)} title="ลบ">✕</button>
        </div>
      ))}

      <div className="cm-edit-totals">
        <div className="cm-edit-totals-line">
          <span>ค่ามือทั้งหมด <b>{money2(Math.max(commission, guarantee) + Number(day.requestFeeTotal || 0))}</b></span>
          <i>|</i>
          <span>รวมรายการพิเศษ <b className={specialTotal < 0 ? 'neg' : ''}>{money2(specialTotal)}</b></span>
        </div>
        <div className="total"><span>ค่ามือที่ต้องจ่าย</span><b>{money2(payable)}</b></div>
      </div>

      <div className="modal-actions cm-edit-actions">
        <button className="btn" onClick={onClose} disabled={busy}>ยกเลิก</button>
        <button className="btn btn-solid" onClick={save} disabled={busy}>
          {busy ? 'กำลังบันทึก…' : 'บันทึก'}
        </button>
      </div>
    </Modal>
  );
}
